
import { useState, useCallback, useEffect } from "react";
import AnimatedCard from "./AnimatedCard";
import InsightCard from "./insights/InsightCard";
import Testimonial from "./Testimonial";
import { insightsData } from "./insights/insightsData";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";

export const CuratedInsightsSection = () => {
  // Filter insights to include 6 tiles: primary 4 (1, 2, 5, 8) + 2 scrollable (9, 10)
  const filteredInsights = insightsData.filter(insight => 
    [11, 1, 2, 5, 8, 9, 10].includes(insight.id)
  );

  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!api) return;
    
    onSelect(api);
    api.on("select", onSelect);
    api.on("reInit", onSelect);
  }, [api, onSelect]);

  return (
    <section className="relative pt-20 pb-12 flex flex-col justify-center items-center px-6 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-cosmic-gradient overflow-hidden z-0"></div>
      <div className="absolute inset-0 bg-grid-pattern opacity-20 z-0"></div>
      <div className="absolute top-1/4 left-1/5 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl z-0" data-depth="0.2"></div>
      <div className="absolute bottom-1/4 right-1/5 w-96 h-96 rounded-full bg-purple-500/20 blur-3xl z-0" data-depth="0.1"></div>
      <div className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full bg-cyan-400/20 blur-2xl z-0" data-depth="0.15"></div>
      
      {/* Extended bottom gradient overlay for smooth transition to next section */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#0c1427] via-[#1a1b3a] to-transparent z-0"></div>
      
      <div className="container mx-auto max-w-6xl relative z-10 py-6">
        <AnimatedCard animation="fade-in">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
              <span className="text-gradient text-glow-blue">Curated</span> <span className="text-white">Insights</span>
            </h2>
            <p className="text-white/70 max-w-3xl mx-auto text-xl">
              Expertly selected articles and insights exploring the impact of AI on contract automation, compliance and risk management, legal research, litigation, e-discovery, and more—crafted to inform and inspire legal professionals.
            </p>
          </div>
        </AnimatedCard>
        
        {/* Desktop Horizontal Carousel - using filtered insights */}
        <div className="hidden lg:block">
          <Carousel
            opts={{
              align: "start",
              loop: false,
            }}
            setApi={setApi}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {filteredInsights.map((insight, index) => (
                <CarouselItem key={insight.id} className="pl-4 md:basis-1/2 lg:basis-1/4 xl:basis-1/4">
                  <InsightCard
                    title={insight.title}
                    description={insight.description}
                    icon={insight.icon}
                    color={insight.color}
                    delay={index * 100}
                    url={insight.url}
                    image={insight.image}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            {canScrollPrev && <CarouselPrevious className="left-2" />}
            {canScrollNext && <CarouselNext className="right-2" />}
          </Carousel>
        </div>
        
        {/* Mobile/Tablet Vertical Insights Layout - also using filtered insights */}
        <div className="lg:hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredInsights.map((insight, index) => (
              <InsightCard
                key={insight.id}
                title={insight.title}
                description={insight.description}
                icon={insight.icon}
                color={insight.color}
                delay={index * 100}
                url={insight.url}
                image={insight.image}
              />
            ))}
          </div>
        </div>
        
        {/* Testimonial within the same cosmic gradient */}
        <div className="container mx-auto max-w-4xl relative z-10 pt-16">
          <div className="flex justify-center">
            <div className="w-full max-w-2xl text-center">
              <Testimonial
                quote="DocketLabs has been instrumental in helping us formulate our AI strategy."
                author="Shaun H. Malvin"
                position="Partner, Avid Legal"
                delay={500}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CuratedInsightsSection;
